import { useMemo } from 'react';
import { useGetDepartmentAnalyticsQuery } from '../../../store/services/report.service';

/**
 * Custom hook for department analytics with enhanced functionality
 * Follows the same pattern as useUserSearch and useSimulationData
 * @param {Object} params - Parameters for the analytics query
 * @param {number} params.tenancyId - The tenancy ID
 * @param {string} params.startDate - Start date in YYYY-MM-DD format (optional)
 * @param {string} params.endDate - End date in YYYY-MM-DD format (optional)
 * @param {boolean} params.skip - Whether to skip the query (optional)
 * @returns {Object} Enhanced analytics data and utilities
 */
export const useDepartmentAnalytics = ({
    tenancyId,
    startDate,
    endDate,
    skip = false
}) => {
    // Calculate default dates (past 6 months) if not provided
    const defaultDates = useMemo(() => {
        const today = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(today.getMonth() - 6);

        return {
            defaultStartDate: sixMonthsAgo.toISOString().split('T')[0],
            defaultEndDate: today.toISOString().split('T')[0]
        };
    }, []);

    // Use provided dates or fall back to defaults
    const queryStartDate = startDate || defaultDates.defaultStartDate;
    const queryEndDate = endDate || defaultDates.defaultEndDate;

    const {
        data: rawData,
        error,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        refetch
    } = useGetDepartmentAnalyticsQuery(
        { tenancyId, startDate: queryStartDate, endDate: queryEndDate },
        { skip: skip || !tenancyId }
    );

    // Memoized processed data
    const processedData = useMemo(() => {
        if (!rawData) return null;

        const { summary, departmentData, locationData, ...rest } = rawData;

        // Calculate additional metrics
        const enhancedSummary = {
            ...summary,
            overallCourseCompletionRate: summary.totalCoursesSent > 0
                ? Math.round((summary.totalCoursesCompleted / summary.totalCoursesSent) * 100 * 100) / 100
                : 0,
            overallSimulationCompletionRate: summary.totalSimulationsSent > 0
                ? Math.round((summary.totalSimulationsCompleted / summary.totalSimulationsSent) * 100 * 100) / 100
                : 0
        };

        // Sort and enhance department data
        const enhancedDepartmentData = departmentData
            ?.map(dept => ({
                ...dept,
                efficiency: dept.userCount > 0
                    ? Math.round(((dept.coursesCompleted + dept.simulationsClicked) / dept.userCount) * 100) / 100
                    : 0
            }))
            .sort((a, b) => b.efficiency - a.efficiency) || [];

        // Sort and enhance location data
        const enhancedLocationData = locationData
            ?.map(location => ({
                ...location,
                efficiency: location.userCount > 0
                    ? Math.round(((location.coursesCompleted + location.simulationsClicked) / location.userCount) * 100) / 100
                    : 0
            }))
            .sort((a, b) => b.efficiency - a.efficiency) || [];

        return {
            ...rest,
            summary: enhancedSummary,
            departmentData: enhancedDepartmentData,
            locationData: enhancedLocationData
        };
    }, [rawData]);

    // Analytics utilities
    const analytics = useMemo(() => {
        if (!processedData) return {};

        const { departmentData, locationData, summary } = processedData;

        return {
            // Top performers
            topDepartmentByCompletion: departmentData?.[0] || null,
            topLocationByCompletion: locationData?.[0] || null,

            // Department insights
            departmentInsights: {
                averageCompletionRate: departmentData?.length > 0
                    ? departmentData.reduce((acc, dept) => acc + dept.courseCompletionRate, 0) / departmentData.length
                    : 0,
                departmentsAboveAverage: departmentData?.filter(dept =>
                    dept.courseCompletionRate > (summary?.overallCourseCompletionRate || 0)
                ).length || 0
            },

            // Location insights  
            locationInsights: {
                averageCompletionRate: locationData?.length > 0
                    ? locationData.reduce((acc, loc) => acc + loc.courseCompletionRate, 0) / locationData.length
                    : 0,
                locationsAboveAverage: locationData?.filter(loc =>
                    loc.courseCompletionRate > (summary?.overallCourseCompletionRate || 0)
                ).length || 0
            }
        };
    }, [processedData]);

    return {
        // Data
        data: processedData,
        rawData,
        analytics,

        // Query states
        isLoading,
        isFetching,
        isSuccess,
        isError,
        error,

        // Actions
        refetch
    };
};

/**
 * Hook for department analytics with automatic date range (previous month)
 * @param {number} tenancyId - The tenancy ID
 * @returns {Object} Analytics data with default date range
 */
export const useDepartmentAnalyticsDefault = (tenancyId) => {
    const { startDate, endDate } = useMemo(() => {
        const currentDate = new Date();
        const firstDayOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDayOfPreviousMonth = new Date(firstDayOfCurrentMonth.getTime() - 1);
        const firstDayOfPreviousMonth = new Date(lastDayOfPreviousMonth.getFullYear(), lastDayOfPreviousMonth.getMonth(), 1);

        return {
            startDate: firstDayOfPreviousMonth.toISOString().split('T')[0],
            endDate: firstDayOfCurrentMonth.toISOString().split('T')[0]
        };
    }, []);

    return useDepartmentAnalytics({ tenancyId, startDate, endDate });
};

export default useDepartmentAnalytics;